/**
 * 通用附件图标组件
 * 根据文件扩展名自动显示对应的类型图标
 */

import { useMemo } from 'react';
// 图标
import WordIcon from '@/assets/icons/file-word.svg?react';
import ExcelIcon from '@/assets/icons/file-excel.svg?react';
import PptIcon from '@/assets/icons/file-ppt.svg?react';
import ImageIcon from '@/assets/icons/file-image.svg?react';
import PdfIcon from '@/assets/icons/file-pdf.svg?react';
import ZipIcon from '@/assets/icons/file-zip.svg?react';
import MarkdownIcon from '@/assets/icons/file-markdown.svg?react';
import OtherIcon from '@/assets/icons/file-other.svg?react';
import TxtIcon from '@/assets/icons/file-text.svg?react';

// 附件后缀类型，根据这个匹配图标
export type AttachmentType =
  | 'doc'
  | 'docx'
  | 'xls'
  | 'xlsx'
  | 'ppt'
  | 'pptx'
  | 'png'
  | 'jpg'
  | 'jpeg'
  | 'gif'
  | 'pdf'
  | 'tar'
  | 'zip'
  | 'rar'
  | 'txt'
  | 'md'
  | 'other';

const attachmentTypeArray: AttachmentType[] = [
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'png',
  'jpg',
  'jpeg',
  'gif',
  'pdf',
  'tar',
  'zip',
  'rar',
  'txt',
  'md',
  'other',
];

/**
 * 根据文件名获取附件类型
 */
export const getAttachmentType = (fileName: string): AttachmentType => {
  if (!fileName) return 'other';

  const nameSplit = fileName.split('.');
  if (nameSplit.length > 1) {
    const extName = nameSplit[nameSplit.length - 1].toLowerCase();
    if (attachmentTypeArray.includes(extName as AttachmentType)) {
      return extName as AttachmentType;
    }
  }
  return 'other';
};

/**
 * 根据附件类型获取图标组件
 */
export const getAttachmentIconComponent = (type: AttachmentType) => {
  if (['doc', 'docx'].includes(type)) {
    return WordIcon;
  } else if (['xls', 'xlsx'].includes(type)) {
    return ExcelIcon;
  } else if (['ppt', 'pptx'].includes(type)) {
    return PptIcon;
  } else if (['png', 'jpg', 'jpeg', 'gif'].includes(type)) {
    return ImageIcon;
  } else if (['pdf'].includes(type)) {
    return PdfIcon;
  } else if (['tar', 'zip', 'rar'].includes(type)) {
    return ZipIcon;
  } else if (['txt'].includes(type)) {
    return TxtIcon;
  } else if (['md'].includes(type)) {
    return MarkdownIcon;
  } else {
    return OtherIcon;
  }
};

interface AttachmentIconProps {
  name: string;
  size?: number;
  className?: string;
}

/**
 * 附件图标组件
 * 自动根据文件名显示对应的类型图标
 */
const AttachmentIcon = ({ name, size = 18, className = '' }: AttachmentIconProps) => {
  const type = useMemo(() => getAttachmentType(name), [name]);

  const IconComponent = useMemo(() => getAttachmentIconComponent(type), [type]);

  return (
    <span className={`inline-flex justify-center items-center ${className}`} style={{ width: size, height: size }}>
      <IconComponent style={{ width: size, height: size }} />
    </span>
  );
};

export default AttachmentIcon;
